import type { StpChartOption, StpChartValueSemantic, StpChartValueSemantics } from '../types'
import type { StpChartTokens } from '../tokens'
import { formatAmount, formatPercent } from '@/utils/misc-tools'

export interface StpLinePolicyContext {
  tokens: StpChartTokens
  valueSemantic: StpChartValueSemantics
  reducedMotion: boolean
}

type ChartRecord = Record<string, unknown>

function toRecords(value: unknown): ChartRecord[] {
  if (Array.isArray(value))
    return value.filter((item): item is ChartRecord => typeof item === 'object' && item !== null)

  return typeof value === 'object' && value !== null ? [value as ChartRecord] : []
}

function fromRecords(records: ChartRecord[], original: unknown): ChartRecord | ChartRecord[] | undefined {
  if (!records.length)
    return undefined

  return Array.isArray(original) ? records : records[0]
}

function normalizeNumber(value: unknown) {
  if (value === null || value === undefined || value === '')
    return undefined

  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

function formatSemanticValue(value: unknown, semantic: StpChartValueSemantic, abbreviated: boolean) {
  const numeric = normalizeNumber(value)
  if (numeric === undefined)
    return '-'

  if (semantic.kind === 'amount')
    return formatAmount({ value: numeric, currency: semantic.currency }, true, abbreviated, abbreviated ? '' : ' ')

  if (semantic.kind === 'ratio')
    return formatPercent(numeric, { fromRatio: semantic.input === 'ratio' })

  return String(numeric)
}

function applyAxisPolicy(axis: ChartRecord, context: StpLinePolicyContext, semantic: StpChartValueSemantic | undefined): ChartRecord {
  const axisLabel = {
    ...(axis.axisLabel as ChartRecord | undefined),
    color: context.tokens.mutedColor,
    fontFamily: context.tokens.fontFamily,
    fontSize: context.tokens.fontSize,
    ...(semantic
      ? { formatter: (value: unknown) => formatSemanticValue(value, semantic, true) }
      : {}),
  }

  return {
    ...axis,
    axisLabel,
    ...(semantic
      ? {
          splitLine: {
            ...(axis.splitLine as ChartRecord | undefined),
            lineStyle: {
              ...((axis.splitLine as ChartRecord | undefined)?.lineStyle as ChartRecord | undefined),
              color: context.tokens.splitLineColor,
              type: 'dashed',
            },
          },
        }
      : {}),
  }
}

function resolveAxisSemantics(yAxes: ChartRecord[], valueSemantic: StpChartValueSemantics) {
  if (!Array.isArray(valueSemantic))
    return yAxes.map(() => valueSemantic)

  if (valueSemantic.length !== yAxes.length)
    throw new Error(`STP line chart value semantics must match yAxis count (${yAxes.length}).`)

  return valueSemantic
}

function getUniformSemantic(valueSemantic: StpChartValueSemantics) {
  const semantics = Array.isArray(valueSemantic) ? valueSemantic : [valueSemantic]
  const first = semantics[0]
  if (!first || !semantics.every(semantic => JSON.stringify(semantic) === JSON.stringify(first)))
    return undefined

  return first
}

function applyLineSeriesPolicy(series: ChartRecord, tokens: StpChartTokens): ChartRecord {
  if (series.type !== 'line')
    return series

  if (typeof series.id !== 'string' || !series.id)
    throw new Error('STP line chart series require a stable id.')

  return {
    ...series,
    smooth: series.smooth ?? true,
    showSymbol: false,
    connectNulls: false,
    lineStyle: {
      ...(series.lineStyle as ChartRecord | undefined),
      width: tokens.lineWidth,
    },
    emphasis: {
      ...(series.emphasis as ChartRecord | undefined),
      lineStyle: {
        ...((series.emphasis as ChartRecord | undefined)?.lineStyle as ChartRecord | undefined),
        width: ((series.emphasis as ChartRecord | undefined)?.lineStyle as ChartRecord | undefined)?.width ?? 'bolder',
      },
    },
  }
}

/** Applies shared semantic-line presentation without mutating the source option. */
export function applyStpLinePolicy(source: StpChartOption, context: StpLinePolicyContext): StpChartOption {
  const option = source as ChartRecord
  const xAxis = toRecords(option.xAxis).map(axis => applyAxisPolicy(axis, context, undefined))
  const originalYAxes = toRecords(option.yAxis)
  const axisSemantics = resolveAxisSemantics(originalYAxes, context.valueSemantic)
  const yAxis = originalYAxes.map((axis, index) => applyAxisPolicy(axis, context, axisSemantics[index]))
  const sourceSeries = toRecords(option.series)
  const series = sourceSeries.map(item => applyLineSeriesPolicy(item, context.tokens))
  const tooltip = option.tooltip as ChartRecord | undefined
  const legends = toRecords(option.legend).map(legend => ({
    ...legend,
    textStyle: {
      ...(legend.textStyle as ChartRecord | undefined),
      fontFamily: context.tokens.fontFamily,
      fontSize: context.tokens.fontSize,
    },
  }))
  const uniformSemantic = getUniformSemantic(context.valueSemantic)

  return {
    ...source,
    color: option.color ?? context.tokens.palette,
    animation: context.reducedMotion ? false : option.animation,
    textStyle: {
      ...(option.textStyle as ChartRecord | undefined),
      fontFamily: context.tokens.fontFamily,
      fontSize: context.tokens.fontSize,
      color: context.tokens.textColor,
    },
    aria: {
      ...(option.aria as ChartRecord | undefined),
      enabled: (option.aria as ChartRecord | undefined)?.enabled ?? true,
    },
    tooltip: {
      ...tooltip,
      textStyle: {
        ...(tooltip?.textStyle as ChartRecord | undefined),
        fontFamily: context.tokens.fontFamily,
        fontSize: context.tokens.fontSize,
      },
      ...(!tooltip?.formatter && uniformSemantic && !tooltip?.valueFormatter
        ? { valueFormatter: (value: unknown) => formatSemanticValue(value, uniformSemantic, false) }
        : {}),
    },
    ...(legends.length ? { legend: fromRecords(legends, option.legend) } : {}),
    ...(xAxis.length ? { xAxis: fromRecords(xAxis, option.xAxis) } : {}),
    ...(yAxis.length ? { yAxis: fromRecords(yAxis, option.yAxis) } : {}),
    ...(series.length ? { series } : {}),
  } as unknown as StpChartOption
}
